import { ReactNode } from 'react';

import {
  DangerBannerStyled,
  InfoPurpleBannerStyled,
  WarningBannerStyled,
  BannerStyledProps,
} from './banner.style';

/**
 * Banner design system component. Renders a panel (BoxPanel) for contextual messages.
 * Use variant components (e.g. DangerBanner, InfoPurpleBanner) for specific styles.
 *
 * @example
 * ```tsx
 * <DangerBanner>
 *   <Text $marginBottom={0}>This will replace existing marks.</Text>
 * </DangerBanner>
 * ```
 */

interface BannerCommonProps {
  children: ReactNode;
  $size?: BannerStyledProps['$size'];
}

/**
 * Danger banner: red9 text on red1 background. Use for warnings or destructive messaging.
 * Layout is provided by BoxPanel; any content (e.g. Text) can be used as children.
 */
export const DangerBanner = ({
  children,
  $size = 'default',
}: BannerCommonProps) => (
  <DangerBannerStyled $size={$size}>{children}</DangerBannerStyled>
);

/**
 * Warning banner: orange9 text on orange1 background. Use for warning messaging.
 * Layout is provided by BoxPanel; any content (e.g. Text) can be used as children.
 */
export const WarningBanner = ({
  children,
  $size = 'default',
}: BannerCommonProps) => (
  <WarningBannerStyled $size={$size}>{children}</WarningBannerStyled>
);

/**
 * Info-purple banner: purple9 text on purple1 background. Use for informational messaging.
 * Layout is provided by BoxPanel; any content (e.g. Text) can be used as children.
 */
export const InfoPurpleBanner = ({
  children,
  $size = 'default',
}: BannerCommonProps) => (
  <InfoPurpleBannerStyled $size={$size}>{children}</InfoPurpleBannerStyled>
);
